import { useCallback, useEffect, useRef, useState } from 'react';

import { AnimationCanvas } from '@suntex.core/animation';
import { Logger } from '@suntex.core/logging';
import { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';
import { GradeLevel } from '@suntex.fim/enums';
import { useTheme } from '@suntex.fim/ui';

import { FIMHub_prog } from '../FIMHub';
import { initAccessibility } from '../accessibility';
import { FIMHub } from '../createjs/FIMHub';
import { HubSettings, ModuleFilter, ModuleTagging } from '../types';

const logger = Logger.for(['CanvasHub']);

export function CanvasHub({
  config,
  path,
  enable,
  disable,
  focus,
  gradeLevel,
  progressSummary,
  settings,
  moduleTagging,
  onPathChange,
  setPath,
  navigate,
}: {
  readonly config: { readonly preview: boolean };
  path: string;
  enable: ModuleFilter;
  disable: ModuleFilter;
  focus: ModuleFilter;
  gradeLevel: GradeLevel | undefined;
  progressSummary: Entities_Player_ProgressSummary_Response | undefined;
  settings: HubSettings;
  readonly moduleTagging: readonly ModuleTagging[];
  onPathChange?: (path: string) => void;
  setPath?: (path: string) => void;
  navigate?: (href: string) => void;
}) {
  const { fonts } = useTheme();
  const [ready, setReady] = useState(false);
  const accessibilityInitIdRef = useRef(0);
  const disposeAccessibilityRef = useRef<(() => void) | null>(null);
  const [starStats, setStarStats] = useState<{
    goldStars: number;
    silverStars: number;
    totalStars: number;
  } | null>(null);

  const updateHub = useCallback(() => {
    if (!window.hub) return;

    window.hub.display({
      path,
      enable,
      disable,
      focus,
      gradeLevel,
      progressSummary,
      settings,
      onPathChange,
      setPath,
      navigate,
    });

    const stats = window.hub.getStarStats();
    setStarStats(stats);
  }, [
    path,
    enable,
    disable,
    focus,
    gradeLevel,
    progressSummary,
    settings,
    onPathChange,
    setPath,
    navigate,
  ]);

  // Update hub when props change (after initial ready)
  useEffect(() => {
    if (ready) {
      updateHub();
    }
  }, [ready, updateHub]);

  // Cleanup on unmount: dispose hub and clear window reference
  useEffect(() => {
    return () => {
      accessibilityInitIdRef.current++;
      disposeAccessibilityRef.current?.();
      disposeAccessibilityRef.current = null;

      if (window.hub) {
        window.hub.dispose();
        window.hub = undefined;
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <AnimationCanvas
        width={600}
        height={600}
        compositionId="E3D4E169DDB59C48A3B46AB2CE777800"
        initGraphics={(cjs, an) => {
          FIMHub(cjs, an);
        }}
        onReady={({ lib, stage }) => {
          const accessibilityInitId = ++accessibilityInitIdRef.current;
          disposeAccessibilityRef.current?.();
          disposeAccessibilityRef.current = null;

          // Dispose existing hub before creating new one (handles StrictMode double-invoke)
          if (window.hub) {
            window.hub.dispose();
            window.hub = undefined;
          }

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const exportRoot = new lib.FIMHub!();
          stage.addChild(exportRoot);
          const hub = FIMHub_prog(stage, lib, {
            ...config,
            fontFamily: fonts.primary.family,
            moduleTagging,
          });

          // Display immediately after creating hub
          // (can't rely on setReady triggering useEffect in StrictMode)
          updateHub();
          void initAccessibility(hub, stage)
            .then((dispose) => {
              if (accessibilityInitIdRef.current === accessibilityInitId) {
                disposeAccessibilityRef.current = dispose;
              } else {
                dispose?.();
              }
            })
            .catch((error: unknown) => {
              if (accessibilityInitIdRef.current === accessibilityInitId) {
                logger.error('Failed to initialize canvas hub accessibility', {
                  error: error instanceof Error ? error.message : String(error),
                });
              }
            });
          setReady(true);
        }}
      />
      {starStats && (
        <div
          style={{
            // For now, this is hidden until we have a better design for it. It will be used to visualize progress within the current path.
            visibility: 'hidden',
            position: 'absolute',
            bottom: 0,
            right: 0,
            textAlign: 'right',
          }}
        >
          {starStats.silverStars > 0 && (
            <div>
              stars: {starStats.silverStars}/{starStats.totalStars}
            </div>
          )}
          {starStats.goldStars > 0 && (
            <div>
              gold stars: {starStats.goldStars}/{starStats.totalStars}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
